import { AppTab } from '@/components/AppTabs';
import Fallback from '@/components/Fallback';
import {
  createSubmitFab,
  createSubmitModal,
  createSubmitTrigger,
  TotalLabel,
} from '@/components/ResourceManage';
import UploadInput from '@/components/UploadInput';
import useAsyncFn, { AsyncState } from '@/hooks/useAsyncFn';
import { LectureResource } from '@/services/admin';
import { Lecture } from '@/services/entity';
import { makeStyles, Tab } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
} from 'antd';
import { FormProps } from 'antd/lib/form';
import { TableProps } from 'antd/lib/table';
import format from 'date-fns/format';
import React from 'react';
import { Link } from 'react-router-dom';

const resource = LectureResource;
type Entity = Lecture;

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

const RecordForm: React.FC<FormProps> = (props) => {
  return (
    <Form labelCol={{ xs: 4 }} wrapperCol={{ xs: 20 }} {...props}>
      <Form.Item label="名称" name="name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label="描述" name="description">
        <Input.TextArea />
      </Form.Item>
      <Form.Item label="链接" name="url" rules={[{ required: true }]}>
        <UploadInput />
      </Form.Item>
    </Form>
  );
};

const SubmitModal = createSubmitModal<Entity>(RecordForm);
const SubmitTrigger = createSubmitTrigger(SubmitModal);
const SubmitFab = createSubmitFab(SubmitTrigger);

interface DataTableProps extends TableProps<Entity> {
  onCreate?: () => void;
  onDelete?: () => void;
}
const DataTable: React.FC<DataTableProps> = ({
  onCreate,
  onDelete,
  ...rest
}) => {
  return (
    <Table
      {...rest}
      rowKey="id"
      bordered
      size="small"
      columns={[
        {
          dataIndex: 'id',
          title: '#',
        },
        {
          dataIndex: 'name',
          title: '名称',
          render: (name, record) => (
            <Link to={`/admin/course/lectures/${record.id}`}>{name}</Link>
          ),
        },
        {
          dataIndex: 'createdAt',
          title: '时间',
          render: (createdAt) =>
            format(new Date(createdAt), 'yyyy-MM-dd HH:mm:ss'),
        },
        {
          title: '操作',
          render: (_, record) => {
            return (
              <>
                <Popconfirm
                  title="确认删除?"
                  onConfirm={async () => {
                    await resource.delete(record.id);
                    onDelete && onDelete();
                  }}
                >
                  <Button type="link">删除</Button>
                </Popconfirm>
                <Divider type="vertical" />
                <SubmitTrigger
                  ModalProps={{
                    title: '编辑视频',
                    centered: true,
                    submit: resource.update(record.id),
                    onSubmitted: onCreate,
                    FormProps: { initialValues: record },
                  }}
                  render={({ open }) => (
                    <Button type="link" onClick={open}>
                      编辑
                    </Button>
                  )}
                />
              </>
            );
          },
        },
      ]}
    />
  );
};

const TabPanel: React.FC<{
  state: AsyncState<{ total: number; data: Entity[] }>;
  fetch: typeof resource.query;
}> = ({ state, fetch }) => {
  const [form] = Form.useForm();
  return (
    <div>
      <Card style={{ marginBottom: 8 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => fetch({ filter: values })}
        >
          <Form.Item label="视频名称" name="$search">
            <Input />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right' }}>
            <Space>
              <Button htmlType="reset" onClick={() => form.resetFields()}>
                重置
              </Button>
              <Button type="primary" htmlType="submit" disabled={state.loading}>
                查询
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Fallback
        state={state}
        initial={<DataTable loading />}
        render={({ data }) => {
          return (
            <DataTable
              dataSource={data}
              loading={state.loading}
              onCreate={state.retry}
              onDelete={state.retry}
            />
          );
        }}
      />
    </div>
  );
};

function useAssistantTab(): AppTab {
  const classes = useStyles();
  const [state, fetch] = useAsyncFn(resource.query, []);
  React.useEffect(() => {
    fetch({});
  }, [fetch]);

  return {
    path: `lectures`,
    tab: <Tab label={<TotalLabel state={state}>视频管理</TotalLabel>} />,
    fab: (
      <SubmitFab
        ModalProps={{
          title: '创建视频',
          centered: true,
          submit: resource.create,
          onSubmitted: state.retry,
        }}
        className={classes.fab}
      >
        <AddIcon />
      </SubmitFab>
    ),
    panel: <TabPanel state={state} fetch={fetch} />,
  };
}

export default useAssistantTab;
